import { useMemo, useState } from 'react'
import { CheckIcon } from '@/components/CheckIcon'
import type { Challenge, ChallengeWeek } from '@/domain/workout'
import type { Progress, SessionRecord } from '@/domain/session'
import {
  amrapScore,
  bestRecordOfWeek,
  crewBonusUsed,
  repsPerRound,
  scoredPhase,
  totalCredits,
} from '@/domain/session'
import { cx } from '@/lib/cx'
import { longDate, mmss } from '@/lib/format'
import styles from './workout.module.css'

type NewSession = Omit<SessionRecord, 'id'>

interface SummaryViewProps {
  challenge: Challenge
  week: ChallengeWeek
  progress: Progress
  /** Rondas contadas durante el AMRAP puntuado. */
  countedRounds: number
  /** Tiempo del finisher, si la sesión tenía uno. */
  afapSeconds: number | null
  onSave: (session: NewSession) => Promise<void>
  onClose: () => void
}

/** Cierre de la sesión: se apunta la marca y se suma al contador. */
export function SummaryView({
  challenge,
  week,
  progress,
  countedRounds,
  afapSeconds,
  onSave,
  onClose,
}: SummaryViewProps) {
  const [rounds, setRounds] = useState(countedRounds)
  const [reps, setReps] = useState(0)
  const [crewBonus, setCrewBonus] = useState(false)
  const [saved, setSaved] = useState<SessionRecord | null>(null)
  const [saving, setSaving] = useState(false)

  const phase = scoredPhase(week)
  const perRound = phase ? repsPerRound(phase) : 0

  const benchmark = useMemo(() => {
    if (!week.benchmarkOf || perRound === 0) return null
    return bestRecordOfWeek(progress, week.benchmarkOf, perRound)
  }, [week.benchmarkOf, progress, perRound])

  const bonusLeft = challenge.bonus ? challenge.bonus.maxUses - crewBonusUsed(progress) : 0
  const canUseBonus = bonusLeft > 0

  const handleSave = async () => {
    if (saving || saved) return
    setSaving(true)
    const session: NewSession = {
      challengeId: challenge.id,
      weekNumber: week.number,
      completedAt: new Date().toISOString(),
      rounds,
      reps,
      afapSeconds,
      crewBonus: crewBonus && canUseBonus,
      credits: crewBonus && canUseBonus ? 2 : 1,
    }
    await onSave(session)
    setSaved({ ...session, id: 'guardada' })
    setSaving(false)
  }

  const credits = totalCredits(progress)
  const missing = Math.max(0, challenge.targetSessions - credits)
  const difference =
    benchmark && perRound > 0 ? amrapScore({ rounds, reps }, perRound) - amrapScore(benchmark, perRound) : null

  return (
    <div className={styles.summary}>
      <h2>
        Sesión
        <em>completada</em>
      </h2>

      <p className={styles.summaryLead}>
        {benchmark
          ? `Tu marca del ${longDate(benchmark.completedAt)}: ${benchmark.rounds} rondas + ${benchmark.reps} reps. Apunta la de hoy.`
          : 'Apunta tu marca del AMRAP. La necesitarás para ver cuánto has mejorado.'}
      </p>

      {!saved && (
        <>
          <div className={styles.fields}>
            <div>
              <label htmlFor="rondas">Rondas AMRAP</label>
              <input
                id="rondas"
                className={styles.number}
                type="number"
                min={0}
                max={99}
                inputMode="numeric"
                value={rounds}
                onChange={(event) => setRounds(Math.max(0, Number(event.target.value) || 0))}
              />
            </div>
            <div>
              <label htmlFor="reps">Reps sueltas</label>
              <input
                id="reps"
                className={styles.number}
                type="number"
                min={0}
                max={999}
                inputMode="numeric"
                value={reps}
                onChange={(event) => setReps(Math.max(0, Number(event.target.value) || 0))}
              />
            </div>
          </div>

          {challenge.bonus && (
            <button
              type="button"
              className={cx(styles.toggle, crewBonus && styles.toggleOn)}
              aria-pressed={crewBonus}
              disabled={!canUseBonus}
              onClick={() => setCrewBonus((value) => !value)}
            >
              <span className={styles.box}>
                <CheckIcon />
              </span>
              <span>
                <b>Crew Bonus ×2</b>
                <span className={styles.toggleNote}>
                  {canUseBonus
                    ? 'He entrenado con alguien de fuera del Crew y mandamos la foto los dos.'
                    : 'Ya has usado los dos bonus del reto.'}
                </span>
              </span>
            </button>
          )}

          <button type="button" className={styles.save} onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar sesión'}
          </button>
        </>
      )}

      {saved && (
        <>
          <div className={styles.receipt}>
            <b>Sesión registrada</b>
            <br />
            Semana {String(week.number).padStart(2, '0')} · AMRAP {saved.rounds} rondas + {saved.reps} reps
            {saved.afapSeconds !== null && (
              <>
                <br />
                Finisher AFAP · {mmss(saved.afapSeconds)}
              </>
            )}
            {saved.crewBonus && (
              <>
                <br />
                Crew Bonus ×2 aplicado
              </>
            )}
            {difference !== null && (
              <>
                <br />
                Marca anterior {benchmark?.rounds}+{benchmark?.reps} → Hoy {saved.rounds}+{saved.reps}
                <br />
                <b>
                  {difference > 0 ? 'Has mejorado' : difference === 0 ? 'Mismo resultado' : 'Sigue ahí'}
                </b>
              </>
            )}
            <br />
            Contador · {Math.min(credits, challenge.targetSessions)} de {challenge.targetSessions}
            <br />
            <b>
              {missing === 0
                ? 'Reto completado'
                : missing === 1
                  ? 'Falta 1 sesión'
                  : `Faltan ${missing} sesiones`}
            </b>
            <br />
            <br />
            Manda la foto al grupo
          </div>

          <button type="button" className={cx(styles.save, styles.ghost)} onClick={onClose}>
            Volver al panel
          </button>
        </>
      )}
    </div>
  )
}
